import React from 'react';
import { Box, Typography, Button, Paper, Alert, CircularProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { BOOKING_THEME } from '../bookingTheme';
import { formatTimeWithZone } from '../../../utils/timezone';
import type { ConfirmationData } from '../hooks/useBookingFlow';
import type { AppointmentTaker } from '../../marketplace/types';

interface StepConfirmationProps {
  taker: AppointmentTaker;
  confirmation: ConfirmationData | null;
  paymentStatus: string;
  paymentError: string | null;
  onRetry?: () => Promise<void>;
}

const StepConfirmation: React.FC<StepConfirmationProps> = ({
  taker,
  confirmation,
  paymentStatus,
  paymentError,
  onRetry,
}) => {
  const navigate = useNavigate();
  const tz = taker.timeZone || 'America/Toronto';
  const isPending = paymentStatus === 'pending';
  const isRetry = paymentStatus === 'retry';

  if (isPending) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CircularProgress sx={{ color: BOOKING_THEME.stepperActive, mb: 2 }} />
        <Typography variant="body1" sx={{ color: BOOKING_THEME.textSecondary }}>
          Confirming your payment...
        </Typography>
      </Box>
    );
  }

  if (isRetry || (paymentError && !confirmation)) {
    return (
      <Box sx={{ textAlign: 'center', py: 2 }}>
        <Alert severity="warning" sx={{ mb: 3, textAlign: 'left' }}>
          <strong>Your payment was received</strong>, but we encountered an issue creating your booking.
          {paymentError && <><br />{paymentError}</>}
        </Alert>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Your payment is safe — please click <strong>Retry</strong> to complete your booking, or contact us if the issue persists.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          {onRetry && (
            <Button
              variant="contained"
              onClick={onRetry}
              sx={{ bgcolor: BOOKING_THEME.stepperActive, color: '#fff', '&:hover': { bgcolor: '#222' } }}
            >
              Retry Booking
            </Button>
          )}
          <Button
            variant="outlined"
            onClick={() => navigate('/chat')}
            sx={{ borderColor: BOOKING_THEME.border, color: BOOKING_THEME.textPrimary }}
          >
            Contact Support
          </Button>
        </Box>
      </Box>
    );
  }

  if (!confirmation) return null;

  const slotDateStr = format(confirmation.slotStart, 'EEEE, MMMM d, yyyy');
  const slotTimeStr = `${formatTimeWithZone(confirmation.slotStart, 'h:mm a', tz)} – ${formatTimeWithZone(confirmation.slotEnd, 'h:mm a', tz)}`;

  return (
    <Box sx={{ textAlign: 'center', py: 1 }}>
      <CheckCircleIcon sx={{ fontSize: 56, color: BOOKING_THEME.stepperDone, mb: 1.5 }} />
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5, color: BOOKING_THEME.textPrimary }}>
        Booking confirmed!
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        A confirmation has been sent to {confirmation.bookerEmail}
      </Typography>

      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          border: `1px solid ${BOOKING_THEME.border}`,
          borderRadius: 2,
          textAlign: 'left',
          backgroundColor: BOOKING_THEME.pageBg,
        }}
      >
        <Row label="Consultant" value={taker.displayName} />
        <Row label="Type" value={confirmation.type === 'walk-in' ? 'Walk-in' : 'Book a call'} />
        <Row label="Date" value={slotDateStr} />
        <Row label="Time" value={slotTimeStr} />
        {confirmation.officeAddress && confirmation.type === 'walk-in' && (
          <Row label="Location" value={confirmation.officeAddress} />
        )}
        <Row label="Reference" value={confirmation.bookingReference.slice(0, 8).toUpperCase()} />
      </Paper>

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          onClick={() => navigate('/chat')}
          sx={{ borderColor: BOOKING_THEME.border, color: BOOKING_THEME.textPrimary }}
        >
          Chat with us
        </Button>
      </Box>
    </Box>
  );
};

const Row: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 0.75 }}>
    <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>{label}</Typography>
    <Typography variant="body2" sx={{ textAlign: 'right', fontWeight: 500 }}>{value}</Typography>
  </Box>
);

export default StepConfirmation;
